// Co-op pointers travel as a spot on the element under them, not as a fraction of the window: the key of
// the nearest landmark or marked-up row above it (a row's data-row-id, a boat's data-vessel-id, <main>, the
// rail, the top bar, an open dialog), the child-index path down from there, the element's tag, and where
// in its box the pointer is. On the other screen that resolves to the same row or button whatever the
// window size or scroll; when it doesn't (a different layout, a row that isn't there), the viewport
// fraction that travels alongside is used instead. Shapes are checked in packages/shared/src/domain/coop.ts.
import { isCoopAnchorKey, type CoopAnchor } from '@technohull/shared';

const KEY_ATTRS = ['data-row-id', 'data-vessel-id', 'data-sap'] as const;
const VALUE_RE = /^[\w-]{1,40}$/;
const MAX_DEPTH = 40;
const MAX_INDEX = 5000; // CoopAnchorSchema's bound: a path past it wouldn't be taken

function keyOf(el: Element): string | null {
  for (const a of KEY_ATTRS) {
    const v = el.getAttribute(a);
    if (v && VALUE_RE.test(v)) return `[${a}="${v}"]`;
  }
  if (el.tagName === 'MAIN') return 'main';
  if (el.id === 'nav-rail') return '#nav-rail';
  if (el.classList.contains('topbar')) return '.topbar';
  if (el.getAttribute('role') === 'dialog') return '[role="dialog"]';
  return null;
}

const frac = (v: number) => Math.round(Math.max(0, Math.min(1, v)) * 1000) / 1000;

/** the anchor for a pointer at (x, y) over `hit`, or null when nothing stable is above it. */
export function anchorOf(hit: Element, x: number, y: number): CoopAnchor | null {
  const path: number[] = [];
  let n: Element = hit;
  for (let depth = 0; depth < MAX_DEPTH; depth++) {
    const k = keyOf(n);
    if (k) {
      const r = hit.getBoundingClientRect();
      return {
        k,
        p: path.reverse(),
        t: hit.tagName.toUpperCase(),
        ax: r.width ? frac((x - r.left) / r.width) : 0.5,
        ay: r.height ? frac((y - r.top) / r.height) : 0.5,
      };
    }
    const parent = n.parentElement;
    if (!parent || parent === n.ownerDocument.body) return null;
    const i = Array.prototype.indexOf.call(parent.children, n) as number;
    if (i > MAX_INDEX) return null;
    path.push(i);
    n = parent;
  }
  return null;
}

/** an anchor on the page's own content (main, or a row it marks up). Clicks, the field being edited and
 *  scroll positions only travel as these: a dialog, the bar or the rail isn't the same on every screen. */
export const isPageAnchor = (a: CoopAnchor): boolean => a.k === 'main' || a.k.startsWith('[data-');

/** the element an anchor points at on this screen; when only that last step isn't here (a cell's editor
 *  open on their screen alone, a button where they have a link), the element holding it. Null when the
 *  path lands anywhere else. */
export function resolveAnchor(a: CoopAnchor, root: ParentNode = document): Element | null {
  if (!isCoopAnchorKey(a.k)) return null;
  let el: Element | null;
  try { el = root.querySelector(a.k); } catch { return null; }
  if (!el) return null;
  for (let d = 0; d < a.p.length; d++) {
    const next: Element | null = el.children[a.p[d]!] ?? null;
    if (!next) return d === a.p.length - 1 ? el : null;
    if (d === a.p.length - 1 && next.tagName.toUpperCase() !== a.t) return el;
    el = next;
  }
  return el.tagName.toUpperCase() === a.t ? el : null;
}
